(ns common.transaction-process.dictionary
  "The field dictionary — the recursive *semantic* layer of a
   transaction-process template (the 'JSON Schema'). One uniform structure: every
   'thing on an entity' is one entry in a map keyed by its item-kw and dispatched
   by `:field/kind`. Four kinds; `entity-slot` and `entity-list` each carry a
   nested dictionary — that is the recursion.

   Each single sub-entity is its own named ref field
   (see `common.schema.entities.transaction-process`), and all per-slot config is
   folded into this one dictionary beside the scalar fields.

   Two firm boundaries:
   - **Semantics only.** Entity-type, data-type, option *values*, rules, nested
     templates live here. Arrangement (stages, cards, grouping, sections, render
     strategy) is a SEPARATE layout layer — never in this map.
   - **Pure data.** Rules are `common.visibility` / `common.mandatory` fragments;
     hooks are registry keywords (never embedded fns). So the dictionary stays
     serializable, diffable, and malli-validatable.

   Validation is opt-in (`validate-dictionary!`), mirroring
   `common.schema.entity-type-registry/validate-registry!`: it turns the implicit
   keyword contracts (kind known, entity-type known) into load-time errors instead
   of silent view-layer misbehaviour."
  (:require
   [common.mandatory :as mandatory]
   [common.transaction-process.hooks :as hooks]
   [common.visibility :as visibility]
   [malli.core :as m]))

(def field-kinds
  "The closed set of dictionary entry kinds. Absent `:field/kind` ⇒
   `:field.kind/value`, so every existing scalar field-config is already a valid
   value-field entry, unchanged.

   - :field.kind/value           — own attribute; plain `(get entity kw)`.
   - :field.kind/attachment-slot — resolved over `:common/attachments` by the
                                   `:common/system-kws` tag (= the slot-id key).
   - :field.kind/entity-slot     — one named ref-one sub-entity; carries a nested
                                   `:slot/template` dictionary (the recursion).
   - :field.kind/entity-list     — N homogeneous sub-entities under a ref-many;
                                   carries a nested `:item-template` dictionary."
  #{:field.kind/value
    :field.kind/attachment-slot
    :field.kind/entity-slot
    :field.kind/entity-list})

(defn entry-kind
  "The kind of a dictionary entry, defaulting to value-field when `:field/kind`
   is absent (so the union dispatch and all callers treat a bare scalar config as
   a value-field)."
  [entry]
  (:field/kind entry :field.kind/value))

(def ^:private Visibility (into [:enum] visibility/visibility-keys))
(def ^:private Mandatoriness (into [:enum] mandatory/mandatoriness-keys))
(def ^:private HookPhase (into [:enum] hooks/lifecycle-phases))

;; Rule fragments stay loose (`map?`): the rule interpreters
;; (common.visibility / common.mandatory) own the operator/query-type vocabulary;
;; the dictionary only carries the data.
(def ^:private Rules [:vector map?])

(def Required
  "Requiredness of a single entity-slot, resolved tri-state (needed / not-needed /
   undecided). Decisions read a first-class per-process field (`:from-field`,
   mapped via `:true-value` / `:false-value`; an unset field ⇒ undecided/nil, so
   the slot is not pre-populated). Attestations/declarations carry no per-process
   override — a static `:default?` boolean, with `:editable?` reserved for a
   future per-process toggle."
  [:map
   [:from-field  {:optional true} keyword?]
   [:true-value  {:optional true} keyword?]
   [:false-value {:optional true} keyword?]
   [:default?    {:optional true} [:maybe boolean?]]
   [:editable?   {:optional true} boolean?]])

(def schema
  "Recursive malli schema for a whole dictionary (item-kw → entry). The four
   entry variants are dispatched on `entry-kind` (absent `:field/kind` ⇒ value).
   Maps are open: value-fields and slots legitimately carry presentation/metadata
   keys (`:field/label`, `:field/options`, `:field/placeholder`, `:field/category`,
   …) that this structural schema does not enumerate — the closed contract is the
   *kind-discriminating* keys."
  [:schema
   {:registry
    {::dictionary [:map-of keyword? [:ref ::entry]]

     ::entry
     [:multi {:dispatch entry-kind}
      [:field.kind/value           [:ref ::value-field]]
      [:field.kind/attachment-slot [:ref ::attachment-slot]]
      [:field.kind/entity-slot     [:ref ::entity-slot]]
      [:field.kind/entity-list     [:ref ::entity-list]]]

     ::value-field
     [:map
      [:field/kind               {:optional true} [:= :field.kind/value]]
      [:field/visibility         {:optional true} Visibility]
      [:field/visibility-rules   {:optional true} Rules]
      [:field/mandatoriness      {:optional true} Mandatoriness]
      [:field/mandatoriness-rules {:optional true} Rules]]

     ::attachment-slot
     [:map
      [:field/kind               [:= :field.kind/attachment-slot]]
      [:field/visibility         {:optional true} Visibility]
      [:field/visibility-rules   {:optional true} Rules]
      [:field/mandatoriness      {:optional true} Mandatoriness]
      [:field/mandatoriness-rules {:optional true} Rules]
      [:field/multiple?          {:optional true} boolean?]
      [:slot/label               {:optional true} string?]]

     ::entity-slot
     [:map
      [:field/kind            [:= :field.kind/entity-slot]]
      [:slot/entity-type      keyword?]
      [:slot/label            {:optional true} string?]
      [:slot/required         {:optional true} Required]
      [:slot/visibility       {:optional true} Visibility]
      [:slot/visibility-rules {:optional true} Rules]
      [:slot/retain-when-hidden? {:optional true} boolean?]
      [:slot/hooks            {:optional true} [:map-of HookPhase keyword?]]
      [:slot/block-on-danger-attestation? {:optional true} boolean?]
      [:slot/approval-permission-field {:optional true} keyword?]
      [:slot/sections         {:optional true} [:vector keyword?]]
      [:slot/template         [:ref ::dictionary]]]

     ::entity-list
     [:map
      [:field/kind            [:= :field.kind/entity-list]]
      [:slot/entity-type      keyword?]
      [:slot/label            {:optional true} string?]
      [:slot/visibility       {:optional true} Visibility]
      [:slot/visibility-rules {:optional true} Rules]
      [:item-template         [:ref ::dictionary]]]}}
   ::dictionary])

(defn nested-template
  "The nested dictionary an entry carries, or nil for a leaf entry. `entity-slot`
   carries `:slot/template`; `entity-list` carries `:item-template`."
  [entry]
  (case (entry-kind entry)
    :field.kind/entity-slot (:slot/template entry)
    :field.kind/entity-list (:item-template entry)
    nil))

(defn approval-permission-field
  "The extra permission field that gates *approving* this slot's sub-entity, or
   nil when the slot has no separate approval gate (the common case).

   A decision slot's ordinary write permission covers preparing the decision; a
   slot that names an approval field additionally requires write access to that
   field before the sub-entity may be moved into, out of, or edited while in its
   ready (non-draft) state. The permission matrix is keyed by field-kw alone and
   every decision slot is built from the same `:decision/*` attributes, so this
   indirection is what lets one slot be approvable by fewer teams than another.

   Both the view gate and the endpoint access check must consult this, or the UI
   and the API will disagree about who may approve."
  [entry]
  (:slot/approval-permission-field entry))

(defn entry-seq
  "Depth-first sequence of `[item-kw entry]` pairs across a dictionary and every
   nested template/item-template. The walk order is unspecified within a level
   (maps are unordered); presentation order is the layout layer's concern."
  [dictionary]
  (mapcat (fn [[k entry]]
            (cons [k entry]
                  (when-let [nested (nested-template entry)]
                    (entry-seq nested))))
          dictionary))

(defn validate
  "Structural malli validation of a whole dictionary. Returns true/false."
  [dictionary]
  (m/validate schema dictionary))

(defn explain
  [dictionary]
  (m/explain schema dictionary))

(defn- entity-type-errors
  "Referential check: every slot's `:slot/entity-type` must be a known entity
   type. Returns a seq of `{:item-kw :entity-type}` for misses. Skipped when
   `known-entity-types` is nil."
  [dictionary known-entity-types]
  (when known-entity-types
    (for [[k entry] (entry-seq dictionary)
          :let [et (:slot/entity-type entry)]
          :when (and et (not (contains? known-entity-types et)))]
      {:item-kw k :entity-type et})))

(defn validate-dictionary!
  "Validate `dictionary` structurally (malli) and, when the corresponding set is
   supplied in `opts`, referentially. Throws ex-info with explain data on a
   structural failure, then on referential failures. Opt-in — call from tests or
   an explicit startup hook, mirroring `entity-type-registry/validate-registry!`.
   Returns the dictionary on success.

   opts:
     :known-entity-types — set of valid `:entity.type/*` keywords; when present,
                           every slot's `:slot/entity-type` must be a member."
  ([dictionary] (validate-dictionary! dictionary {}))
  ([dictionary {:keys [known-entity-types]}]
   (when-not (validate dictionary)
     (throw (ex-info "Invalid transaction-process field dictionary"
                     {:errors (explain dictionary)})))
   (let [et-errs (entity-type-errors dictionary known-entity-types)]
     (when (seq et-errs)
       (throw (ex-info "Dictionary slot references unknown entity type(s)"
                       {:errors (vec et-errs)}))))
   dictionary))
